import { TrendingUp, Banknote, CreditCard, Smartphone, Landmark, Building, ArrowDownLeft, Wallet, Receipt, ChevronRight } from "lucide-react";
import { formatCompact, formatCurrency } from "./paymentsConstants";

/* ─── Tiny mode pill inside a card ─── */
function ModePill({ label, value, active, onClick, color, negate }) {
  if (value === 0 && !active) return null;
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
        active
          ? `${color?.activeBg || "bg-white"} ${color?.activeBorder || "border-white/60"} shadow-md scale-105 ring-2 ring-white/40`
          : `bg-white/40 border-white/20 hover:bg-white/60 hover:border-white/40`
      }`}
      data-testid={`chip-${label.toLowerCase().replace(/[/ ]/g, "-")}`}
    >
      <span className={active ? "text-slate-900" : "text-white/90"}>{label}</span>
      <span className={`font-black tabular-nums ${negate ? "text-rose-200" : active ? "text-slate-900" : "text-white"}`}>
        {negate ? `−${formatCompact(value)}` : formatCompact(value)}
      </span>
    </button>
  );
}

/* ─── Grand Total mini stat ─── */
function MiniStat({ label, value }) {
  return (
    <div className="text-center px-4">
      <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest block">{label}</span>
      <p className="text-xl font-black text-white mt-0.5">{value}</p>
    </div>
  );
}

const COLLECTION_MODES = [
  { key: "cash", label: "Cash", icon: Banknote, color: { activeBg: "bg-emerald-100", activeBorder: "border-emerald-300" } },
  { key: "card", label: "Card", icon: CreditCard, color: { activeBg: "bg-blue-100", activeBorder: "border-blue-300" } },
  { key: "upi", label: "UPI", icon: Smartphone, color: { activeBg: "bg-violet-100", activeBorder: "border-violet-300" } },
  { key: "neft_rtgs", label: "NEFT/RTGS", icon: Building, color: { activeBg: "bg-cyan-100", activeBorder: "border-cyan-300" } },
  { key: "eft", label: "EFT", icon: Landmark, color: { activeBg: "bg-amber-100", activeBorder: "border-amber-300" } },
  { key: "cheque", label: "Cheque", icon: Receipt, color: { activeBg: "bg-teal-100", activeBorder: "border-teal-300" } },
  { key: "netbanking", label: "Netbank", icon: Building, color: { activeBg: "bg-sky-100", activeBorder: "border-sky-300" } },
  { key: "payout", label: "Payout", icon: ArrowDownLeft, negate: true, color: { activeBg: "bg-rose-100", activeBorder: "border-rose-300" } },
];

export function PaymentKPIs({ grandTotals, modeFilter, onModeClick }) {
  const g = grandTotals;
  const collectionTotal = (g.cash || 0) + (g.card || 0) + (g.upi || 0) + (g.neft_rtgs || 0) + (g.eft || 0) + (g.cheque || 0) + (g.netbanking || 0);
  const debtorsTotal = g.credit || 0;
  const advancesTotal = g._advanceTotal || 0;

  const collectionModes = ["collection","cash","card","debit_card","credit_card","upi","neft_rtgs","eft","cheque","netbanking","payout"];
  const debtorsModes = ["debtors","credit","credit_only","self_credit"];
  const advancesModes = ["advances"];
  const hasFilter = !!modeFilter;
  const isCollectionMode = collectionModes.includes(modeFilter);
  const isDebtorsMode = debtorsModes.includes(modeFilter);
  const isAdvancesMode = advancesModes.includes(modeFilter);

  // Count non-zero collection modes for summary display
  const activeCollModes = COLLECTION_MODES.filter(m => (g[m.key] || 0) > 0 && !m.negate);

  return (
    <div className="space-y-4" data-testid="kpi-section">

      {/* ═══ COLLECTION CARD ═══ */}
      <div
        onClick={() => onModeClick && onModeClick("collection")}
        role="button" tabIndex={0}
        className={`w-full text-left relative overflow-hidden rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 cursor-pointer ${
          hasFilter && !isCollectionMode ? "opacity-40 scale-[0.99]" : ""
        } ${modeFilter === "collection" ? "ring-4 ring-emerald-300/50" : ""}`}
        data-testid="section-collection"
      >
        <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-6">
          {/* Decorative */}
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-white/[0.06]" />

          {/* Header */}
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-100/80">Collection</span>
                <p className="text-3xl font-black text-white tracking-tight leading-none mt-0.5">{formatCompact(collectionTotal)}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30" />
          </div>

          {/* Mode pills row */}
          {activeCollModes.length > 0 && (
            <div className="relative flex flex-wrap gap-2 mt-2">
              {COLLECTION_MODES.map(m => {
                const val = g[m.key] || 0;
                if (val === 0 && !m.negate) return null;
                return (
                  <ModePill key={m.key} label={m.label} value={val} negate={m.negate}
                    active={modeFilter === m.key} color={m.color}
                    onClick={(e) => { e.stopPropagation(); onModeClick && onModeClick(m.key); }}
                  />
                );
              })}
              {/* Card sub-breakdown */}
              {((g.debit_card || 0) > 0 || (g.credit_card || 0) > 0) && (
                <>
                  {(g.debit_card || 0) > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); onModeClick("debit_card"); }}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                        modeFilter === "debit_card" ? "bg-white border-blue-300 text-blue-700 shadow-md" : "bg-blue-800/30 border-blue-400/30 text-blue-100 hover:bg-blue-700/40"
                      }`}>Debit: {formatCurrency(g.debit_card)}</button>
                  )}
                  {(g.credit_card || 0) > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); onModeClick("credit_card"); }}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                        modeFilter === "credit_card" ? "bg-white border-blue-300 text-blue-700 shadow-md" : "bg-blue-800/30 border-blue-400/30 text-blue-100 hover:bg-blue-700/40"
                      }`}>Credit Card: {formatCurrency(g.credit_card)}</button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ DEBTORS CARD ═══ */}
      <div
        onClick={() => onModeClick && onModeClick("debtors")}
        role="button" tabIndex={0}
        className={`w-full text-left relative overflow-hidden rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 cursor-pointer ${
          hasFilter && !isDebtorsMode ? "opacity-40 scale-[0.99]" : ""
        } ${modeFilter === "debtors" ? "ring-4 ring-indigo-300/50" : ""}`}
        data-testid="section-patient-debtors"
      >
        <div className="bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700 p-6">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-white/[0.06]" />

          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm">
                <Receipt className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-100/80">Patient Debtors</span>
                <p className="text-3xl font-black text-white tracking-tight leading-none mt-0.5">{formatCompact(debtorsTotal)}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30" />
          </div>

          {/* Credit + Self Credit pills */}
          <div className="relative flex flex-wrap gap-2 mt-2">
            {(g.credit_only || 0) > 0 && (
              <ModePill label="Credit" value={g.credit_only || 0}
                active={modeFilter === "credit_only"} color={{ activeBg: "bg-indigo-100", activeBorder: "border-indigo-300" }}
                onClick={(e) => { e.stopPropagation(); onModeClick && onModeClick("credit_only"); }} />
            )}
            {(g.self_credit || 0) > 0 && (
              <ModePill label="Self Credit" value={g.self_credit || 0}
                active={modeFilter === "self_credit"} color={{ activeBg: "bg-purple-100", activeBorder: "border-purple-300" }}
                onClick={(e) => { e.stopPropagation(); onModeClick && onModeClick("self_credit"); }} />
            )}
          </div>
        </div>
      </div>

      {/* ═══ ADVANCES CARD ═══ */}
      <div
        onClick={() => onModeClick && onModeClick("advances")}
        role="button" tabIndex={0}
        className={`w-full text-left relative overflow-hidden rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 cursor-pointer ${
          hasFilter && !isAdvancesMode ? "opacity-40 scale-[0.99]" : ""
        } ${modeFilter === "advances" ? "ring-4 ring-amber-300/50" : ""}`}
        data-testid="section-advances"
      >
        <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 p-6">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-white/[0.06]" />

          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-amber-100/80">Advances</span>
                <p className="text-3xl font-black text-white tracking-tight leading-none mt-0.5">{formatCompact(advancesTotal)}</p>
                {(g._advanceCount || 0) > 0 && (
                  <span className="text-[11px] text-white/50 mt-0.5 block">{g._advanceCount} advance orders</span>
                )}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30" />
          </div>

          {/* Advance mode breakdown */}
          {advancesTotal > 0 && (
            <div className="relative flex flex-wrap gap-2 mt-2">
              {(g._advanceCash || 0) > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-white/30 border border-white/20 text-white text-xs font-bold">Cash: {formatCurrency(g._advanceCash)}</span>
              )}
              {(g._advanceCard || 0) > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-white/30 border border-white/20 text-white text-xs font-bold">Card: {formatCurrency(g._advanceCard)}</span>
              )}
              {(g._advanceUPI || 0) > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-white/30 border border-white/20 text-white text-xs font-bold">UPI: {formatCurrency(g._advanceUPI)}</span>
              )}
              {(g._advanceNEFT || 0) > 0 && (
                <span className="px-3 py-1.5 rounded-xl bg-white/30 border border-white/20 text-white text-xs font-bold">NEFT: {formatCurrency(g._advanceNEFT)}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ GRAND TOTAL BAR ═══ */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl px-8 py-5 flex items-center justify-between text-white shadow-xl">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Grand Total</span>
            <p className="text-3xl font-black tracking-tight" data-testid="kpi-total-collection">{formatCompact(g.total)}</p>
          </div>
          <div className="h-10 w-px bg-slate-700" />
          <MiniStat label="Txns" value={(g.txn_count || 0).toLocaleString("en-IN")} />
          <MiniStat label="Bills" value={(g.bill_count || 0).toLocaleString("en-IN")} />
        </div>
        <div className="flex items-center gap-3">
          {(g.wallet || 0) > 0 && (
            <button onClick={() => onModeClick && onModeClick("wallet")}
              className={`text-sm font-bold px-4 py-2 rounded-xl transition-all ${modeFilter === "wallet" ? "bg-amber-500 text-white shadow-lg" : "bg-slate-700/80 text-amber-400 hover:bg-slate-700 border border-slate-600"}`}
              data-testid="kpi-wallet">Wallet: {formatCurrency(g.wallet)}</button>
          )}
          {(g.others || 0) > 0 && (
            <button onClick={() => onModeClick && onModeClick("others")}
              className={`text-sm font-bold px-4 py-2 rounded-xl transition-all ${modeFilter === "others" ? "bg-orange-500 text-white shadow-lg" : "bg-slate-700/80 text-orange-400 hover:bg-slate-700 border border-slate-600"}`}
              data-testid="kpi-others">Others: {formatCurrency(g.others)}</button>
          )}
        </div>
      </div>
    </div>
  );
}
